import { addDays, dayLabelShort, isSameDay, startOfWeek } from '../../lib/datetime'
import { CalendarEventTypeColor, CalendarEventTypeLabel, type CalendarEventDto } from '@toga/shared'

const START_HOUR = 8
const END_HOUR = 21
const HOUR_HEIGHT_PX = 56

interface Props {
  cursor: Date
  events: CalendarEventDto[]
  onPickSlot: (date: Date) => void
  onPickEvent: (event: CalendarEventDto) => void
}

export default function WeekView({ cursor, events, onPickSlot, onPickEvent }: Props) {
  const monday = startOfWeek(cursor)
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i))
  const hours = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => START_HOUR + i)

  const today = new Date()

  return (
    <div className="surface overflow-hidden">
      {/* Header de días */}
      <div className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] border-b border-base">
        <div className="px-2 py-2 text-[10px] uppercase tracking-wider t-faint">Hora</div>
        {days.map((d) => {
          const todayCol = isSameDay(d, today)
          return (
            <div
              key={d.toISOString()}
              className={[
                'px-2 py-2 text-center border-l border-base/40',
                todayCol ? 'bg-toga-500/10' : '',
              ].join(' ')}
            >
              <div className="text-[10px] uppercase tracking-wider t-faint">{dayLabelShort(d)}</div>
              <div
                className={['text-sm font-semibold', todayCol ? 'text-toga-300' : 't-primary'].join(
                  ' '
                )}
              >
                {d.getDate()}
              </div>
            </div>
          )
        })}
      </div>

      <div
        className="grid grid-cols-[64px_repeat(7,minmax(0,1fr))] relative"
        style={{ height: HOUR_HEIGHT_PX * hours.length }}
      >
        {/* Columna de horas */}
        <div className="relative">
          {hours.map((h) => (
            <div
              key={h}
              className="text-[10px] t-faint pr-2 text-right border-t border-base/30"
              style={{ height: HOUR_HEIGHT_PX }}
            >
              {String(h).padStart(2, '0')}:00
            </div>
          ))}
        </div>

        {days.map((d) => (
          <DayColumn
            key={d.toISOString()}
            day={d}
            hours={hours}
            events={events.filter((e) => eventOverlapsDay(e, d))}
            onPickSlot={(hour) => {
              const slot = new Date(d)
              slot.setHours(hour, 0, 0, 0)
              onPickSlot(slot)
            }}
            onPickEvent={onPickEvent}
          />
        ))}
      </div>
    </div>
  )
}

function eventOverlapsDay(e: CalendarEventDto, d: Date): boolean {
  const start = new Date(e.startAt)
  const end = e.endAt ? new Date(e.endAt) : new Date(start.getTime() + 60 * 60 * 1000)
  const dayStart = new Date(d)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(d)
  dayEnd.setHours(23, 59, 59, 999)
  return start < dayEnd && end > dayStart
}

function DayColumn({
  day,
  hours,
  events,
  onPickSlot,
  onPickEvent,
}: {
  day: Date
  hours: number[]
  events: CalendarEventDto[]
  onPickSlot: (hour: number) => void
  onPickEvent: (event: CalendarEventDto) => void
}) {
  return (
    <div className="relative border-l border-base/40">
      {/* Slots por hora */}
      {hours.map((h) => (
        <button
          key={h}
          type="button"
          onClick={() => onPickSlot(h)}
          className="block w-full hover:bg-toga-500/5 border-t border-base/30"
          style={{ height: HOUR_HEIGHT_PX }}
          aria-label={`Crear evento a las ${h}:00`}
        />
      ))}
      {/* Eventos absolutos */}
      {events.map((e) => {
        const placement = placeEvent(e, day, hours)
        if (!placement) return null
        const color = CalendarEventTypeColor[e.type]
        return (
          <button
            key={e.id}
            type="button"
            onClick={() => onPickEvent(e)}
            className={[
              'absolute left-1 right-1 px-1.5 py-1 rounded text-left text-[11px] border',
              color.bg,
              color.text,
              color.border,
              'hover:brightness-125 transition overflow-hidden',
            ].join(' ')}
            style={{ top: placement.top, height: placement.height }}
            title={`${CalendarEventTypeLabel[e.type]} · ${e.title}`}
          >
            <div className="font-semibold truncate">{e.title}</div>
            {placement.height > 28 && (
              <div className="text-[10px] opacity-80 truncate">{formatRange(e)}</div>
            )}
            {e.caseCode && placement.height > 42 && (
              <div className="text-[10px] opacity-70 truncate font-mono">{e.caseCode}</div>
            )}
          </button>
        )
      })}
    </div>
  )
}

function placeEvent(
  e: CalendarEventDto,
  day: Date,
  hours: number[]
): { top: number; height: number } | null {
  const startHour = hours[0]!
  const lastHour = hours[hours.length - 1]!
  const dayStart = new Date(day)
  dayStart.setHours(startHour, 0, 0, 0)
  const dayEnd = new Date(day)
  dayEnd.setHours(lastHour + 1, 0, 0, 0)

  const eStart = new Date(e.startAt)
  const eEnd = e.endAt ? new Date(e.endAt) : new Date(eStart.getTime() + 60 * 60 * 1000)

  const start = eStart < dayStart ? dayStart : eStart
  const end = eEnd > dayEnd ? dayEnd : eEnd
  if (end <= dayStart || start >= dayEnd) return null

  const top = ((start.getHours() - startHour) * 60 + start.getMinutes()) * (HOUR_HEIGHT_PX / 60)
  const heightMin = (end.getTime() - start.getTime()) / 60000
  return { top, height: Math.max(20, heightMin * (HOUR_HEIGHT_PX / 60)) }
}

function formatRange(e: CalendarEventDto): string {
  if (e.allDay) return 'Todo el día'
  const start = new Date(e.startAt).toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  })
  if (!e.endAt) return start
  const end = new Date(e.endAt).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })
  return `${start}–${end}`
}
